import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Table,
  Group,
  Text,
  Anchor,
  Center,
  TextInput,
  Stack,
  Button as MantineButton,
  Checkbox,
  Paper,
} from "@mantine/core";
import { Button, Modal } from "@/components/common";
import { filtersApi } from "@/api/client";
import type { Filter } from "@/api/types";
import { BulkEditDrawer } from "./BulkEditDrawer";

interface FilterListProps {
  filters: Filter[];
}

export function FilterList({ filters }: FilterListProps) {
  const [duplicateModal, setDuplicateModal] = useState<Filter | null>(null);
  const [duplicateName, setDuplicateName] = useState("");
  const [duplicateChannelId, setDuplicateChannelId] = useState("");
  const [duplicateSearchArea, setDuplicateSearchArea] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkEditOpened, setBulkEditOpened] = useState(false);

  const queryClient = useQueryClient();

  useEffect(() => {
    const availableIds = new Set(filters.map((filter) => filter.id));
    // Selection is local UI state that must be reconciled when server data changes.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedIds((current) => {
      const next = new Set([...current].filter((id) => availableIds.has(id)));
      return next.size === current.size ? current : next;
    });
  }, [filters]);

  const toggleMutation = useMutation({
    mutationFn: (id: number) => filtersApi.toggle(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["filters"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => filtersApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["filters"] });
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: ({
      id,
      name,
      channelId,
      searchArea,
    }: {
      id: number;
      name: string;
      channelId: string;
      searchArea: string;
    }) => filtersApi.duplicate(id, name, channelId, searchArea || null),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["filters"] });
      setDuplicateModal(null);
      setDuplicateName("");
      setDuplicateChannelId("");
      setDuplicateSearchArea("");
    },
  });

  const openDuplicateModal = (filter: Filter) => {
    setDuplicateModal(filter);
    setDuplicateName(`${filter.name} (copy)`);
    setDuplicateChannelId(filter.discord_channel_id);
    setDuplicateSearchArea(filter.search_area || "");
  };

  const handleDuplicate = () => {
    if (duplicateModal && duplicateName && duplicateChannelId) {
      duplicateMutation.mutate({
        id: duplicateModal.id,
        name: duplicateName,
        channelId: duplicateChannelId,
        searchArea: duplicateSearchArea,
      });
    }
  };

  const allSelected = filters.length > 0 && selectedIds.size === filters.length;
  const selectedFilters = filters.filter((filter) => selectedIds.has(filter.id));

  const toggleAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(filters.map((filter) => filter.id)));
  };

  const toggleSelected = (id: number) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (filters.length === 0) {
    return (
      <Center py="xl">
        <Text c="dimmed">
          No filters found.{" "}
          <Anchor component={Link} to="/filters/new" c="primary">
            Create one
          </Anchor>
        </Text>
      </Center>
    );
  }

  return (
    <>
      {selectedIds.size > 0 && (
        <Paper withBorder p="sm" mb="md" style={{ borderColor: "#0f3460" }}>
          <Group justify="space-between">
            <Text>
              {selectedIds.size} filter{selectedIds.size === 1 ? "" : "s"} selected
            </Text>
            <MantineButton onClick={() => setBulkEditOpened(true)}>Edit selected</MantineButton>
          </Group>
        </Paper>
      )}
      <Table.ScrollContainer minWidth={800}>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={44}>
                <Checkbox
                  aria-label="Select all filters"
                  checked={allSelected}
                  indeterminate={selectedIds.size > 0 && !allSelected}
                  onChange={toggleAll}
                />
              </Table.Th>
              <Table.Th>Name</Table.Th>
              <Table.Th>City</Table.Th>
              <Table.Th>Rent Range</Table.Th>
              <Table.Th>Rooms</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {filters.map((filter) => (
              <Table.Tr key={filter.id}>
                <Table.Td>
                  <Checkbox
                    aria-label={`Select ${filter.name}`}
                    checked={selectedIds.has(filter.id)}
                    onChange={() => toggleSelected(filter.id)}
                  />
                </Table.Td>
                <Table.Td>{filter.name}</Table.Td>
                <Table.Td>
                  <Text c="dimmed">{filter.search_area || "Any"}</Text>
                </Table.Td>
                <Table.Td>
                  <Text c="dimmed">
                    {filter.min_rent || 0} - {filter.max_rent || "∞"} kr
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text c="dimmed">
                    {filter.min_rooms || 0} - {filter.max_rooms || "∞"}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text
                    c={filter.is_active ? "green" : "dimmed"}
                    style={{ cursor: "pointer" }}
                    onClick={() => toggleMutation.mutate(filter.id)}
                  >
                    {filter.is_active ? "Active" : "Inactive"}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Group gap="xs">
                    <MantineButton
                      size="xs"
                      variant="filled"
                      color="#0f3460"
                      component={Link}
                      to="/filters/$id/edit"
                      params={{ id: filter.id.toString() }}
                    >
                      Edit
                    </MantineButton>
                    <MantineButton
                      size="xs"
                      variant="filled"
                      color="#0f3460"
                      onClick={() => openDuplicateModal(filter)}
                    >
                      Duplicate
                    </MantineButton>
                    <MantineButton
                      size="xs"
                      variant="filled"
                      color="red"
                      onClick={() => {
                        if (confirm("Are you sure you want to delete this filter?")) {
                          deleteMutation.mutate(filter.id);
                        }
                      }}
                      disabled={deleteMutation.isPending}
                    >
                      Delete
                    </MantineButton>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      <BulkEditDrawer
        opened={bulkEditOpened}
        filters={selectedFilters}
        onClose={() => setBulkEditOpened(false)}
        onApplied={() => {
          setBulkEditOpened(false);
          setSelectedIds(new Set());
        }}
      />

      <Modal
        isOpen={!!duplicateModal}
        onClose={() => setDuplicateModal(null)}
        title="Duplicate Filter"
      >
        <Stack gap="md">
          <TextInput
            label="New Filter Name"
            value={duplicateName}
            onChange={(e) => setDuplicateName(e.target.value)}
          />
          <TextInput
            label="Discord Channel ID"
            value={duplicateChannelId}
            onChange={(e) => setDuplicateChannelId(e.target.value)}
          />
          <TextInput
            label="Search Area"
            placeholder="e.g., Vasastaden~Stockholm_Municipality~se"
            value={duplicateSearchArea}
            onChange={(e) => setDuplicateSearchArea(e.target.value)}
          />
          <Group gap="sm">
            <Button onClick={handleDuplicate} isLoading={duplicateMutation.isPending}>
              Duplicate
            </Button>
            <Button variant="secondary" onClick={() => setDuplicateModal(null)}>
              Cancel
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
